import { Database } from '@nozbe/watermelondb';

import EntryUser from '../../EntryUser';
import InventoryChange from '../../InventoryChange';
import InventoryProduct from '../../InventoryProduct';
import Procedure from '../../Procedure';
import {
    DBServiceOptionsWithImagesAndValidation,
    DBServiceOptionsWithImagesLoggerAndValidation,
} from '../../../../types/dbService';
import {
    EntryModel,
    EntryPayload,
    EntryProcedureToAdd,
    EntryProductToAdd,
    PreparedDataOnEntryUpdate,
} from '../../../../types/Entries';
import { EntryProcedureModel } from '../../../../types/EntryProcedure';
import { EntryProductModel } from '../../../../types/EntryProduct';
import { EntryUserModel } from '../../../../types/EntryUser';
import { InventoryChangeModel } from '../../../../types/InventoryChange';
import { InventoryProductModel } from '../../../../types/InventoryProduct';
import { DBWriterLoggerFunction } from '../../../../types/logger';
import { getUpdatedFieldValue } from '../../../utils';
import { toPreparedDataItems } from './common';

const prepareUpdateEntry = ({
    entryElement,
    payload,
}: {
    entryElement: EntryModel;
    payload: EntryPayload;
}) => {
    return entryElement.prepareUpdate((entry) => {
        entry.horse.id = payload.horseId;
        entry.eventId =
            entryElement.loggedTime !== payload.loggedTime
                ? ''
                : entryElement.eventId;
        entry.invoiceId = payload.invoiceId || entryElement.invoiceId;
        entry.loggedTime = payload.loggedTime;
        entry.title = payload.title;
        entry.notes = getUpdatedFieldValue(payload.notes, entryElement.notes);
        entry.privateNotes = getUpdatedFieldValue(
            payload.privateNotes,
            entryElement.privateNotes,
        );
        entry.userId = entryElement.userId;
        entry.organisationId = entryElement.organisationId;
    });
};

const prepareUpdateEntryProcedures = async ({
    entryElement,
    entryProcedures,
    initialEntryProcedures,
    serviceOptions,
    database,
}: {
    entryElement: EntryModel;
    entryProcedures: EntryProcedureToAdd[];
    initialEntryProcedures: EntryProcedureModel[];
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
    database: Database;
}) => {
    const entryProceduresToAdd: EntryProcedureModel[] = [];
    const entryProceduresToUpdate: EntryProcedureModel[] = [];
    const entryProceduresToDelete: EntryProcedureModel[] = [];

    const proceduresService = new Procedure(serviceOptions);
    const entryProceduresCollection =
        database.collections.get<EntryProcedureModel>('entry_procedures');

    await Promise.all(
        entryProcedures.map(async (entryProcedure) => {
            const initialEntryProcedure = initialEntryProcedures.find(
                (initial) => initial.procedureId === entryProcedure.procedureId,
            );

            if (!initialEntryProcedure) {
                const procedure = await proceduresService.getByID(
                    entryProcedure.procedureId,
                );

                if (!!procedure) {
                    const entryProcedureToAdd =
                        await entryProceduresCollection.prepareCreate(
                            (entryProc: EntryProcedureModel) => {
                                entryProc.entryId = entryElement.id;
                                entryProc.procedureId =
                                    entryProcedure.procedureId;
                                entryProc.price = entryProcedure.price ?? '';
                                entryProc.quantity = entryProcedure.quantity;
                                entryProc.name = procedure.name;
                                entryProc.userId = entryElement.userId;
                                entryProc.organisationId =
                                    entryElement.organisationId;
                            },
                        );
                    entryProceduresToAdd.push(entryProcedureToAdd);
                }
            }
        }),
    );

    await Promise.all(
        entryProcedures.map(async (entryProcedure) => {
            const initialEntryProcedure = initialEntryProcedures.find(
                (initial) => initial.procedureId === entryProcedure.procedureId,
            );

            if (
                initialEntryProcedure &&
                (initialEntryProcedure.quantity !== entryProcedure.quantity ||
                    initialEntryProcedure.price !== entryProcedure.price)
            ) {
                const entryProcedureToUpdate =
                    await initialEntryProcedure.prepareUpdate((entryProc) => {
                        entryProc.price =
                            entryProcedure.price || entryProc.price;
                        entryProc.quantity = entryProcedure.quantity;
                    });

                entryProceduresToUpdate.push(entryProcedureToUpdate);
            }
        }),
    );

    await Promise.all(
        initialEntryProcedures.map(async (initialEntryProcedure) => {
            if (
                !entryProcedures.find(
                    (entryProcedure) =>
                        entryProcedure.procedureId ===
                        initialEntryProcedure.procedureId,
                )
            ) {
                entryProceduresToDelete.push(
                    initialEntryProcedure.prepareMarkAsDeleted(),
                );
            }
        }),
    );

    return {
        entryProceduresToAdd,
        entryProceduresToUpdate,
        entryProceduresToDelete,
    };
};

const prepareRevertInventoryChangeForEntryProduct = async ({
    entryProduct,
    oldLoggedTime,
    newLoggedTime,
    userId,
    serviceOptions,
}: {
    entryProduct: EntryProductModel;
    oldLoggedTime: string;
    newLoggedTime: string;
    userId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
}) => {
    const inventoryChangesService = new InventoryChange(serviceOptions);
    const inventoryProductService = new InventoryProduct(serviceOptions);

    const inventoryProduct = await inventoryProductService.getByParam(
        'product_id',
        entryProduct.productId,
    );

    const revertChange = await inventoryChangesService.prepareAdd(
        {
            inventoryProductId: inventoryProduct[0].id,
            quantityChange: entryProduct.quantity,
            processAt: new Date(oldLoggedTime),
        },
        userId,
    );

    const newChange = await inventoryChangesService.prepareAdd(
        {
            inventoryProductId: inventoryProduct[0].id,
            quantityChange: -entryProduct.quantity,
            processAt: new Date(newLoggedTime),
        },
        userId,
    );

    return [revertChange, newChange];
};

const prepareUpdateEntryProducts = async ({
    entryElement,
    entryProducts,
    initialEntryProducts,
    initialLoggedTime,
    loggedTime,
    userId,
    serviceOptions,
    database,
}: {
    entryElement: EntryModel;
    entryProducts: EntryProductToAdd[];
    initialEntryProducts: EntryProductModel[];
    initialLoggedTime: string;
    loggedTime: string;
    userId: string;
    serviceOptions: DBServiceOptionsWithImagesAndValidation;
    database: Database;
}) => {
    const entryProductsToAdd: EntryProductModel[] = [];
    const entryProductsToUpdate: EntryProductModel[] = [];
    const entryProductsToDelete: EntryProductModel[] = [];
    const inventoryProductsToAdd: InventoryProductModel[] = [];
    const inventoryChanges: InventoryChangeModel[] = [];

    const inventoryProductService = new InventoryProduct(serviceOptions);
    const inventoryChangesService = new InventoryChange(serviceOptions);
    const entryProductsCollection =
        database.collections.get<EntryProductModel>('entry_products');
    const inventoryProductsCollection =
        database.collections.get<InventoryProductModel>('inventory_products');

    await Promise.all(
        entryProducts.map(async (entryProduct) => {
            const initialEntryProduct = initialEntryProducts.find(
                (initial) => initial.productId === entryProduct.productId,
            );

            if (!initialEntryProduct) {
                const inventoryProduct =
                    await inventoryProductService.getByParam(
                        'product_id',
                        entryProduct.productId,
                    );
                if (inventoryProduct.length === 1) {
                    const inventoryChange =
                        await inventoryChangesService.prepareAdd(
                            {
                                inventoryProductId: inventoryProduct[0].id,
                                quantityChange: -entryProduct.quantity,
                                processAt: new Date(loggedTime),
                            },
                            userId,
                        );

                    inventoryChanges.push(inventoryChange);
                } else {
                    const inventoryProductToAdd =
                        await inventoryProductsCollection.prepareCreate(
                            (inventoryProd: InventoryProductModel) => {
                                inventoryProd.productId =
                                    entryProduct.productId;
                                inventoryProd.userId = entryElement.userId;
                                inventoryProd.organisationId =
                                    entryElement.organisationId;
                                inventoryProd.quantity = 0;
                                inventoryProd.favourite = false;
                            },
                        );

                    inventoryProductsToAdd.push(inventoryProductToAdd);

                    const inventoryChange =
                        await inventoryChangesService.prepareAdd(
                            {
                                inventoryProductId: inventoryProductToAdd.id,
                                quantityChange: -entryProduct.quantity,
                                processAt: new Date(loggedTime),
                            },
                            userId,
                        );

                    inventoryChanges.push(inventoryChange);
                }

                const entryProductToAdd =
                    await entryProductsCollection.prepareCreate((entryProd) => {
                        entryProd.entryId = entryElement.id;
                        entryProd.productId = entryProduct.productId;
                        entryProd.quantity = entryProduct.quantity;
                        entryProd.userId = entryElement.userId;
                        entryProd.organisationId = entryElement.organisationId;
                    });
                entryProductsToAdd.push(entryProductToAdd);
            } else {
                if (initialEntryProduct.quantity !== entryProduct.quantity) {
                    const inventoryProduct =
                        await inventoryProductService.getByParam(
                            'product_id',
                            entryProduct.productId,
                        );

                    if (inventoryProduct.length === 1) {
                        const inventoryChange =
                            await inventoryChangesService.prepareAdd(
                                {
                                    inventoryProductId: inventoryProduct[0].id,
                                    quantityChange:
                                        initialEntryProduct.quantity -
                                        entryProduct.quantity,
                                    processAt: new Date(loggedTime),
                                },
                                userId,
                            );

                        inventoryChanges.push(inventoryChange);
                    }

                    const entryProductToUpdate =
                        await initialEntryProduct.prepareUpdate((entryProd) => {
                            entryProd.quantity = entryProduct.quantity;
                        });
                    entryProductsToUpdate.push(entryProductToUpdate);
                }
            }
        }),
    );

    await Promise.all(
        initialEntryProducts.map(async (initialEntryProduct) => {
            if (
                !entryProducts.find(
                    (entryProduct) =>
                        entryProduct.productId ===
                        initialEntryProduct.productId,
                )
            ) {
                entryProductsToDelete.push(
                    initialEntryProduct.prepareMarkAsDeleted(),
                );
                const inventoryProduct =
                    await inventoryProductService.getByParam(
                        'product_id',
                        initialEntryProduct.productId,
                    );
                if (inventoryProduct.length === 1) {
                    const inventoryChange =
                        await inventoryChangesService.prepareAdd(
                            {
                                inventoryProductId: inventoryProduct[0].id,
                                quantityChange: initialEntryProduct.quantity,
                                processAt: new Date(initialLoggedTime),
                            },
                            userId,
                        );

                    inventoryChanges.push(inventoryChange);
                }
            } else if (initialLoggedTime !== loggedTime) {
                const revert =
                    await prepareRevertInventoryChangeForEntryProduct({
                        entryProduct: initialEntryProduct,
                        oldLoggedTime: initialLoggedTime,
                        newLoggedTime: loggedTime,
                        userId,
                        serviceOptions,
                    });

                inventoryChanges.push(...revert);
            }
        }),
    );

    return {
        entryProductsToAdd,
        entryProductsToUpdate,
        entryProductsToDelete,
        inventoryProductsToAdd,
        inventoryChanges,
    };
};

const prepareUpdateEntryUsers = async ({
    entryId,
    membersIds,
    serviceOptions,
}: {
    entryId: string;
    membersIds: string[];
    serviceOptions: DBServiceOptionsWithImagesLoggerAndValidation;
}) => {
    const entryUserService = new EntryUser(serviceOptions);
    const currentEntryUsers = await entryUserService
        .getByEntryId(entryId)
        .fetch();
    const currentUsersIDs = currentEntryUsers.map(
        (currentUser) => currentUser.userId,
    );

    const membersIdsToDelete = currentEntryUsers
        .filter(
            (currentEntryUser) => !membersIds.includes(currentEntryUser.userId),
        )
        .map((entryUser) => entryUser.id);

    const membersIdsToAdd = membersIds.filter(
        (memberId) => !currentUsersIDs.includes(memberId),
    );

    const entryUsersToDelete = await entryUserService.prepareDeleteBatch(
        membersIdsToDelete,
    );
    const entryUsersToAdd = await entryUserService.prepareAddBatch(
        membersIdsToAdd,
        entryId,
    );

    return { entryUsersToAdd, entryUsersToDelete };
};

const appendPreparedEntryProcedures = (
    preparedData: PreparedDataOnEntryUpdate[],
    proceduresData: Awaited<ReturnType<typeof prepareUpdateEntryProcedures>>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            proceduresData.entryProceduresToAdd,
            'entry_procedures',
            'add',
        ),
        ...toPreparedDataItems(
            proceduresData.entryProceduresToUpdate,
            'entry_procedures',
            'update',
        ),
        ...toPreparedDataItems(
            proceduresData.entryProceduresToDelete,
            'entry_procedures',
            'delete',
        ),
    ];
};

const appendPreparedEntryProducts = (
    preparedData: PreparedDataOnEntryUpdate[],
    productsData: Awaited<ReturnType<typeof prepareUpdateEntryProducts>>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(
            productsData.inventoryProductsToAdd,
            'inventory_products',
            'add',
        ),
        ...toPreparedDataItems(
            productsData.entryProductsToAdd,
            'entry_products',
            'add',
        ),
        ...toPreparedDataItems(
            productsData.entryProductsToUpdate,
            'entry_products',
            'update',
        ),
        ...toPreparedDataItems(
            productsData.entryProductsToDelete,
            'entry_products',
            'delete',
        ),
        ...toPreparedDataItems(
            productsData.inventoryChanges,
            'inventory_changes',
            'add',
        ),
    ];
};

const appendPreparedEntryUsers = (
    preparedData: PreparedDataOnEntryUpdate[],
    usersData: Awaited<ReturnType<typeof prepareUpdateEntryUsers>>,
) => {
    return [
        ...preparedData,
        ...toPreparedDataItems(usersData.entryUsersToAdd, 'entry_users', 'add'),
        ...toPreparedDataItems(
            usersData.entryUsersToDelete,
            'entry_users',
            'delete',
        ),
    ];
};

type UpdateRelatedDataBuckets = {
    entryProceduresToAdd: EntryProcedureModel[];
    entryProceduresToUpdate: EntryProcedureModel[];
    entryProceduresToDelete: EntryProcedureModel[];
    entryProductsToAdd: EntryProductModel[];
    entryProductsToUpdate: EntryProductModel[];
    entryProductsToDelete: EntryProductModel[];
    inventoryProductsToAdd: InventoryProductModel[];
    inventoryChanges: InventoryChangeModel[];
    entryUsersToAdd: EntryUserModel[];
    entryUsersToDelete: EntryUserModel[];
};

const logUpdateDBActions = ({
    entryElement,
    buckets,
    logDBAction,
    tableName,
}: {
    entryElement: EntryModel;
    buckets: UpdateRelatedDataBuckets;
    logDBAction: DBWriterLoggerFunction;
    tableName: string;
}) => {
    const {
        entryProceduresToAdd,
        entryProceduresToUpdate,
        entryProceduresToDelete,
        entryProductsToAdd,
        entryProductsToUpdate,
        entryProductsToDelete,
        inventoryProductsToAdd,
        inventoryChanges,
        entryUsersToAdd,
        entryUsersToDelete,
    } = buckets;

    logDBAction({
        message: 'Update entry',
        modelName: tableName,
        payload: entryElement,
    });
    logDBAction({
        message: 'Entry update - create entry procedures',
        modelName: tableName,
        payload: entryProceduresToAdd,
    });
    logDBAction({
        message: 'Entry update - update entry procedures',
        modelName: tableName,
        payload: entryProceduresToUpdate,
    });
    logDBAction({
        message: 'Entry update - delete entry procedures',
        modelName: tableName,
        payload: entryProceduresToDelete,
    });
    logDBAction({
        message: 'Entry update - create entry products',
        modelName: tableName,
        payload: entryProductsToAdd,
    });
    logDBAction({
        message: 'Entry update - update entry products',
        modelName: tableName,
        payload: entryProductsToUpdate,
    });
    logDBAction({
        message: 'Entry update - delete entry products',
        modelName: tableName,
        payload: entryProductsToDelete,
    });
    logDBAction({
        message: 'Entry update - create inventory products',
        modelName: tableName,
        payload: inventoryProductsToAdd,
    });
    logDBAction({
        message: 'Entry update - inventory changes',
        modelName: tableName,
        payload: inventoryChanges,
    });
    logDBAction({
        message: 'Entry update - entry users',
        modelName: tableName,
        payload: [...entryUsersToDelete, ...entryUsersToAdd],
    });
};

export {
    appendPreparedEntryProcedures,
    appendPreparedEntryProducts,
    appendPreparedEntryUsers,
    logUpdateDBActions,
    prepareRevertInventoryChangeForEntryProduct,
    prepareUpdateEntry,
    prepareUpdateEntryProcedures,
    prepareUpdateEntryProducts,
    prepareUpdateEntryUsers,
};

export type { UpdateRelatedDataBuckets };
